import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Logo } from '@/components/Logo';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error('Too many attempts. Please try again in a few minutes.');
      } else {
        // Always show generic success even on errors to avoid revealing existence
        setDone(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-forgot-password-page" className="min-h-screen sb-hero-bg grid place-items-center px-4">
      <div className="max-w-md w-full sb-card sb-gold-glow p-7">
        <div className="flex justify-center"><Logo variant="header" /></div>
        <h1 className="font-display text-3xl text-white mt-5 inline-flex items-center gap-2"><Mail size={20} className="text-[hsl(var(--sb-gold))]" /> FORGOT PASSWORD</h1>
        <p className="text-sm text-white/65 mt-1">Enter your admin email and we’ll send you a secure link to reset your password.</p>

        {done ? (
          <div className="mt-6 p-4 rounded-md border border-[hsl(var(--sb-green))]/30 bg-[hsl(var(--sb-green))]/10">
            <div className="flex items-center gap-2 text-[hsl(var(--sb-green))]"><CheckCircle2 size={18} /><span className="font-display text-lg">CHECK YOUR EMAIL</span></div>
            <p className="text-sm text-white/80 mt-2">If an account exists, a reset link has been sent. The link will expire in 60 minutes and can be used only once.</p>
            <p className="text-xs text-white/50 mt-3">Email sending is currently stubbed. Your reset link has been logged in the backend logs and in the Email Log page.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-password-form">
            <label className="block">
              <span className="text-[11px] uppercase tracking-widest text-white/55">Email</span>
              <input data-testid="forgot-password-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]" />
            </label>
            <button data-testid="forgot-password-submit" type="submit" disabled={submitting} className="w-full h-12 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] disabled:opacity-60">
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        )}

        <div className="mt-5 text-center">
          <Link to="/admin/login" className="inline-flex items-center gap-1 text-sm text-white/65 hover:text-[hsl(var(--sb-gold))]"><ArrowLeft size={14} /> Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
